import { useState } from 'react';
import BulkChecklistChips from './BulkChecklistChips';
import {
    getDisplay,
    getRowMeta,
    getRowPhotoUrl,
    getRowRate,
    getRowSpots,
    hasGeocodeWarning,
} from './groupings';
import { AlertIcon, ChevronIcon, RateIcon, SpotIcon } from './icons';

function RowPhoto({ url }) {
    const [errored, setErrored] = useState(false);
    const showPlaceholder = !url || errored;

    return (
        <div className="props-row-photo" aria-hidden="true">
            {showPlaceholder ? (
                <div className="props-row-photo-placeholder">
                    <span>No Photos Yet</span>
                </div>
            ) : (
                <img
                    src={url}
                    alt=""
                    loading="lazy"
                    referrerPolicy="no-referrer"
                    onError={() => setErrored(true)}
                />
            )}
        </div>
    );
}

export default function PropertyRow({ listing, onNavigate, onNavigateEdit }) {
    const v5Status = listing._v5Status;
    const { name, type, address: addr } = getDisplay(listing);
    const spots = getRowSpots(listing);
    const rate = getRowRate(listing);

    const stats = [];
    if (spots != null) {
        stats.push(
            <span key="spots" className="stat">
                <SpotIcon />
                <strong>{spots}</strong> {spots === 1 ? 'space' : 'spaces'}
            </span>
        );
    }
    if (rate != null) {
        stats.push(
            <span key="rate" className="stat">
                <RateIcon />
                <strong>${Number(rate).toFixed(2)}</strong>/hr
            </span>
        );
    }
    if (v5Status === 'live' && listing.revenue) {
        stats.push(
            <span key="revenue" className="stat" style={{ color: 'var(--green-dark)' }}>
                <strong>{listing.revenue}</strong> this month
            </span>
        );
    }
    if (v5Status === 'draft') {
        // TODO: PP-121 — surface checklist completion %
        stats.push(
            <span key="draft" className="stat" style={{ fontStyle: 'italic' }}>
                Incomplete Draft
            </span>
        );
    }

    const meta = getRowMeta(listing);
    const photoUrl = getRowPhotoUrl(listing);

    const addrChip = (v5Status === 'bulk_draft' && hasGeocodeWarning(listing))
        ? <span className="props-row-addr-chip">⚠ Address needs review</span>
        : null;

    const rowClass = [
        'props-row',
        v5Status === 'attention' ? 'attention' : '',
        v5Status === 'bulk_draft' ? 'bulk-draft' : '',
        v5Status === 'bulk_draft' && listing._bulkReady ? 'ready' : '',
    ].filter(Boolean).join(' ');

    const reasonText = listing.reviewNotes || 'Your listing needs changes before it can go live.';

    return (
        <div
            className={rowClass}
            onClick={() => onNavigate(listing)}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onNavigate(listing);
                }
            }}
            role="button"
            tabIndex={0}
        >
            <div className="props-row-main">
                <RowPhoto url={photoUrl} />
                <div className="props-row-content">
                    <div className="props-row-name">
                        <h4>{name}</h4>
                        <div className="props-row-addr">
                            {addr}
                            {type ? ` · ${type}` : ''}
                            {addrChip}
                        </div>
                    </div>
                    {v5Status === 'bulk_draft' && <BulkChecklistChips listing={listing} />}
                </div>
                <div className="props-row-stats">{stats}</div>
                <div className="props-row-meta">{meta}</div>
                <span className="props-row-chev"><ChevronIcon /></span>
            </div>

            {v5Status === 'attention' && (
                <div className="props-reason-banner" onClick={e => e.stopPropagation()} role="presentation">
                    <div className="props-reason-banner-icon"><AlertIcon /></div>
                    <div className="props-reason-banner-body">
                        <div className="props-reason-banner-title">Reason from Ops</div>
                        <div className="props-reason-banner-text">{reasonText}</div>
                    </div>
                    <div className="props-reason-banner-actions">
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={(e) => {
                                e.stopPropagation();
                                onNavigate(listing);
                            }}
                        >
                            View details
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={(e) => {
                                e.stopPropagation();
                                onNavigateEdit(listing);
                            }}
                        >
                            Edit &amp; Resubmit →
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
